import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getComments,
  updateCommentStatus,
  deleteComment,
  getCommentSettings,
  updateCommentSettings,
  getNewsListForModeration,
  getBlogListForModeration
} from '../apis/commentService';
import { toast } from 'react-toastify';
import { MessageSquare, Check, EyeOff, Eye, Trash2, Loader2, Filter, Settings, Save, Search, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const PAGE_SIZES = [10, 20, 50];

const CommentsModerationPage = () => {
  const [comments, setComments] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [articleTypeFilter, setArticleTypeFilter] = useState('');
  const [articleIdFilter, setArticleIdFilter] = useState('');
  const [articleTitleFilter, setArticleTitleFilter] = useState('');
  const [authorNameFilter, setAuthorNameFilter] = useState('');
  const [bodySearchFilter, setBodySearchFilter] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [actioningId, setActioningId] = useState(null);
  const [allowAnonymousComments, setAllowAnonymousComments] = useState(true);
  const [showAnonymousComments, setShowAnonymousComments] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [articleDropdownOpen, setArticleDropdownOpen] = useState(false);
  const [articleDropdownType, setArticleDropdownType] = useState('news');
  const [articleSearchQuery, setArticleSearchQuery] = useState('');
  const [articleSearchResults, setArticleSearchResults] = useState([]);
  const [articleSearchLoading, setArticleSearchLoading] = useState(false);
  const articleSearchDebounce = useRef(null);
  const articleDropdownRef = useRef(null);

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      if (statusFilter) params.status = statusFilter;
      if (articleTypeFilter) params.articleType = articleTypeFilter;
      if (articleIdFilter) params.articleId = articleIdFilter;
      if (authorNameFilter.trim()) params.authorName = authorNameFilter.trim();
      if (bodySearchFilter.trim()) params.bodySearch = bodySearchFilter.trim();
      if (dateFromFilter) params.dateFrom = dateFromFilter;
      if (dateToFilter) params.dateTo = dateToFilter;
      const res = await getComments(params);
      if (res.success && res.data) {
        setComments(res.data.comments || []);
        setPagination(prev => ({
          ...prev,
          ...res.data.pagination,
          page: res.data.pagination?.page ?? prev.page,
          limit: res.data.pagination?.limit ?? prev.limit,
          total: res.data.pagination?.total ?? 0,
          pages: res.data.pagination?.pages ?? 0
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load comments');
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.page,
    pagination.limit,
    statusFilter,
    articleTypeFilter,
    articleIdFilter,
    authorNameFilter,
    bodySearchFilter,
    dateFromFilter,
    dateToFilter
  ]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Close article dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (articleDropdownRef.current && !articleDropdownRef.current.contains(e.target)) {
        setArticleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Article dropdown: search news or blog when query changes (debounced)
  useEffect(() => {
    if (!articleDropdownOpen) return;
    if (articleSearchDebounce.current) clearTimeout(articleSearchDebounce.current);
    articleSearchDebounce.current = setTimeout(async () => {
      setArticleSearchLoading(true);
      try {
        const fn = articleDropdownType === 'news' ? getNewsListForModeration : getBlogListForModeration;
        const res = await fn(articleSearchQuery);
        setArticleSearchResults(res.success && res.data ? res.data : []);
      } catch (e) {
        setArticleSearchResults([]);
      } finally {
        setArticleSearchLoading(false);
      }
    }, 300);
    return () => {
      if (articleSearchDebounce.current) clearTimeout(articleSearchDebounce.current);
    };
  }, [articleDropdownOpen, articleDropdownType, articleSearchQuery]);

  const openArticleDropdown = (type) => {
    setArticleDropdownType(type);
    setArticleSearchQuery('');
    setArticleSearchResults([]);
    setArticleDropdownOpen(true);
    setArticleSearchLoading(true);
    const fn = type === 'news' ? getNewsListForModeration : getBlogListForModeration;
    fn('').then((res) => {
      setArticleSearchResults(res.success && res.data ? res.data : []);
    }).finally(() => setArticleSearchLoading(false));
  };

  const selectArticle = (id, title) => {
    setArticleIdFilter(id);
    setArticleTitleFilter(title);
    setArticleTypeFilter(articleDropdownType);
    setArticleDropdownOpen(false);
    setPagination(p => ({ ...p, page: 1 }));
  };

  const clearArticleFilter = () => {
    setArticleIdFilter('');
    setArticleTitleFilter('');
    setArticleTypeFilter('');
    setArticleDropdownOpen(false);
    setPagination(p => ({ ...p, page: 1 }));
  };

  const applyTextAndDateFilters = () => {
    setPagination(p => ({ ...p, page: 1 }));
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getCommentSettings();
        if (res.success && res.data) {
          setAllowAnonymousComments(res.data.allowAnonymousComments !== false);
          setShowAnonymousComments(res.data.showAnonymousComments !== false);
        }
      } catch (e) {
        console.error('Failed to load comment settings', e);
      } finally {
        setSettingsLoading(false);
      }
    };
    load();
  }, []);

  const handleSaveCommentSettings = async () => {
    setSettingsSaving(true);
    try {
      const res = await updateCommentSettings({ allowAnonymousComments, showAnonymousComments });
      if (res.success) {
        toast.success('Comment settings saved.');
      } else {
        toast.error(res.message || 'Failed to save settings');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleStatusChange = async (commentId, status, previousStatus) => {
    setActioningId(commentId);
    try {
      const res = await updateCommentStatus(commentId, status);
      if (res.success) {
        if (status === 'APPROVED') {
          toast.success(previousStatus === 'HIDDEN' ? 'Comment unhidden.' : 'Comment approved.');
        } else {
          toast.success('Comment hidden.');
        }
        fetchComments();
      } else {
        toast.error(res.message || 'Failed to update');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setActioningId(null);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Delete this comment permanently?')) return;
    setActioningId(commentId);
    try {
      const res = await deleteComment(commentId);
      if (res.success) {
        toast.success('Comment deleted');
        fetchComments();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setActioningId(null);
    }
  };

  const articleUrl = (c) => {
    if (c.articleType === 'news') return `/news-details?id=${c.articleId}`;
    return `/blog/${c.articleId}`;
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <MessageSquare className="mr-2" size={28} />
          Comment moderation
        </h1>
      </div>

      {/* Main comments control: allow or disallow anonymous comments */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
          <Settings size={18} />
          Comment settings
        </h2>
        {settingsLoading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="animate-spin" size={18} />
            Loading settings…
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allowAnonymousComments}
                onChange={(e) => setAllowAnonymousComments(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Allow anonymous comments (name optional)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAnonymousComments}
                onChange={(e) => setShowAnonymousComments(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show anonymous comments on articles</span>
            </label>
            <button
              type="button"
              onClick={handleSaveCommentSettings}
              disabled={settingsSaving}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {settingsSaving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              Save settings
            </button>
          </div>
        )}
      </div>

      <div className="space-y-4 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter size={18} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters</span>
        </div>

        {/* Article: dropdown with search */}
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 w-24">Article:</span>
            <div className="relative" ref={articleDropdownRef}>
              {articleIdFilter ? (
                <div className="flex items-center gap-2 min-w-[200px] max-w-md">
                  <span className="text-sm text-gray-800 truncate" title={articleTitleFilter}>
                    {articleTypeFilter === 'news' ? 'News: ' : 'Blog: '}{articleTitleFilter || articleIdFilter}
                  </span>
                  <button
                    type="button"
                    onClick={clearArticleFilter}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setArticleDropdownOpen(!articleDropdownOpen)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                  >
                    Select news or blog <ChevronDown size={14} />
                  </button>
                  {articleDropdownOpen && (
                    <div className="absolute left-0 top-full mt-1 z-20 w-80 max-h-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                      <div className="p-2 border-b flex gap-2">
                        <button
                          type="button"
                          onClick={() => openArticleDropdown('news')}
                          className={`px-2 py-1 text-sm rounded ${articleDropdownType === 'news' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'}`}
                        >
                          News
                        </button>
                        <button
                          type="button"
                          onClick={() => openArticleDropdown('blog')}
                          className={`px-2 py-1 text-sm rounded ${articleDropdownType === 'blog' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'}`}
                        >
                          Blog
                        </button>
                      </div>
                      <input
                        type="text"
                        placeholder={`Search ${articleDropdownType}...`}
                        value={articleSearchQuery}
                        onChange={(e) => setArticleSearchQuery(e.target.value)}
                        className="w-full px-2 py-1.5 border-b text-sm"
                        autoFocus
                      />
                      <div className="overflow-y-auto max-h-48">
                        {articleSearchLoading ? (
                          <div className="p-2 text-sm text-gray-500 flex items-center gap-1">
                            <Loader2 className="animate-spin" size={16} /> Loading…
                          </div>
                        ) : articleSearchResults.length === 0 ? (
                          <div className="p-2 text-sm text-gray-500">No results</div>
                        ) : (
                          articleSearchResults.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => selectArticle(item.id, item.title)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 truncate"
                              title={item.title}
                            >
                              {item.title}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Type:</span>
            <select
              value={articleTypeFilter}
              onChange={(e) => {
                setArticleTypeFilter(e.target.value);
                if (e.target.value === '') {
                  setArticleIdFilter('');
                  setArticleTitleFilter('');
                }
                setPagination(p => ({ ...p, page: 1 }));
              }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="news">News</option>
              <option value="blog">Blog</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(p => ({ ...p, page: 1 }));
              }}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="">All</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="HIDDEN">Hidden</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Author:</span>
            <input
              type="text"
              placeholder="Name contains…"
              value={authorNameFilter}
              onChange={(e) => setAuthorNameFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyTextAndDateFilters()}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-40"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Text:</span>
            <input
              type="text"
              placeholder="Words in comment…"
              value={bodySearchFilter}
              onChange={(e) => setBodySearchFilter(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyTextAndDateFilters()}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm w-40"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Date from:</span>
            <input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Date to:</span>
            <input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-2 py-1.5 text-sm"
            />
          </div>

          <button
            type="button"
            onClick={applyTextAndDateFilters}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Search size={14} /> Apply filters
          </button>
        </div>
      </div>

      {/* Page size */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Per page:</span>
          <select
            value={pagination.limit}
            onChange={(e) => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-blue-600" size={32} />
        </div>
      ) : comments.length === 0 ? (
        <p className="text-gray-500 py-8 text-center">No comments match the filters.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Author</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comments.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-sm">
                      <Link
                        to={articleUrl(c)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {c.articleType} #{c.articleId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{c.authorName || 'Anonymous'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">{c.body}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          c.status === 'APPROVED'
                            ? 'bg-green-100 text-green-800'
                            : c.status === 'PENDING'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(c.createdAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {actioningId === c.id ? (
                        <Loader2 className="animate-spin inline-block" size={18} />
                      ) : (
                        <span className="flex items-center justify-end gap-1">
                          {c.status === 'PENDING' && (
                            <button
                              onClick={() => handleStatusChange(c.id, 'APPROVED', c.status)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                              title="Approve"
                            >
                              <Check size={18} />
                            </button>
                          )}
                          {c.status === 'HIDDEN' && (
                            <button
                              onClick={() => handleStatusChange(c.id, 'APPROVED', c.status)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Unhide"
                            >
                              <Eye size={18} />
                            </button>
                          )}
                          {c.status !== 'HIDDEN' && (
                            <button
                              onClick={() => handleStatusChange(c.id, 'HIDDEN')}
                              className="p-1.5 text-gray-600 hover:bg-gray-50 rounded"
                              title="Hide"
                            >
                              <EyeOff size={18} />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(c.id)}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pagination.pages > 1 || pagination.total > 0) && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-gray-600">
                Showing {comments.length === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1}–
                {(pagination.page - 1) * pagination.limit + comments.length} of {pagination.total}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setPagination(p => ({ ...p, page: 1 }))}
                  disabled={pagination.page <= 1}
                  className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  First
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
                  disabled={pagination.page <= 1}
                  className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-2 py-1 text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: Math.min(p.pages, p.page + 1) }))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Next
                </button>
                <button
                  onClick={() => setPagination(p => ({ ...p, page: p.pages }))}
                  disabled={pagination.page >= pagination.pages}
                  className="px-2 py-1 border border-gray-300 rounded text-sm disabled:opacity-50"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommentsModerationPage;
